import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Grid,
  Divider,
  Stack,
  Chip,
  Skeleton,
  Tooltip as MuiTooltip
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  ErrorOutline,
  ArrowUpward,
  ArrowDownward,
  Paid,
  BarChart as BarChartIcon,
  Download,
  TableChart,
  AttachMoney,
  Schedule
} from "@mui/icons-material";

const PayableAgeingAnalysis = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");
  
  const [data, setData] = useState({
    current: 0,
    overdue30: 0,
    overdue60: 0,
    overdue90: 0,
    overdue120: 0,
    totalPayables: 0
  });

  const [stats, setStats] = useState({
    totalPayables: 0,
    overduePercentage: 0,
    currentPercentage: 0,
    avgOverdueDays: 0,
    oldestOverdue: 0,
    riskScore: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("bar");
  const [prevData, setPrevData] = useState(null);

  const CONFIG = {
    colors: {
      primary: [
        colors.greenAccent[500],
        colors.blueAccent[500],
        colors.blueAccent[200],
        colors.redAccent[500],
        colors.redAccent[700]
      ],
      highlight: colors.blueAccent[700],
      tooltipBg: theme.palette.background.paper,
      cardBg: theme.palette.background.default,
      success: colors.greenAccent[500],
      warning: colors.redAccent[500],
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary
    }
  };

  const generateSampleData = () => {
    return {
      current: 5867,
      overdue30: 11734,
      overdue60: 0,
      overdue90: 0,
      overdue120: 0,
      totalPayables: 17601
    };
  };

  const calculateStats = (data) => {
    const totalOverdue = data.overdue30 + data.overdue60 + data.overdue90 + data.overdue120;
    const totalPayables = data.current + totalOverdue;
    
    return {
      totalPayables,
      overduePercentage: Math.round((totalOverdue / totalPayables) * 100),
      currentPercentage: Math.round((data.current / totalPayables) * 100),
      avgOverdueDays: Math.round(
        (data.overdue30 * 30 + data.overdue60 * 60 + data.overdue90 * 90 + data.overdue120 * 120) / 
        (totalOverdue || 1)
      ),
      oldestOverdue: data.overdue120 > 0 ? 120 : 
                    data.overdue90 > 0 ? 90 : 
                    data.overdue60 > 0 ? 60 : 
                    data.overdue30 > 0 ? 30 : 0,
      riskScore: Math.min(100, Math.round(
        (totalOverdue / totalPayables) * 100 + 
        (data.overdue90 + data.overdue120) / totalPayables * 50
      ))
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);
      setPrevData(data);
      
      // Using the correct endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/ageingAnalysisofPayable`,
        { cmpid },
        { 
          headers: { 
            Authorization: token,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      // Check if API data is empty or invalid
      if (!response.data || !response.data["0"] || 
          (Object.values(response.data["0"]).every(val => !val || val === 0))) {
        // Use sample data when API returns empty data
        const sampleData = generateSampleData();
        setData(sampleData);
        setStats(calculateStats(sampleData));
        setIsUsingSampleData(true);
        setError("No data available. Showing sample data.");
        return;
      }

      const transformedData = {
        current: parseFloat(response.data["0"].current) || 0,
        overdue30: parseFloat(response.data["0"].overdue30) || 0,
        overdue60: parseFloat(response.data["0"].overdue60) || 0,
        overdue90: parseFloat(response.data["0"].overdue90) || 0,
        overdue120: parseFloat(response.data["0"].overdue120) || 0,
        totalPayables: parseFloat(response.data["0"].totalPayables) || 0
      };
      
      setData(transformedData);
      setStats(calculateStats(transformedData));
    } catch (err) {
      console.error("API Error:", err);
      setError(err.response?.data?.message || err.message || "Failed to load data");
      const sampleData = generateSampleData();
      setData(sampleData);
      setStats(calculateStats(sampleData));
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "bar" ? "table" : "bar");
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;
      const total = stats.totalPayables;
      const percentage = Math.round((payload[0].value / total) * 100);
      
      return (
        <Card sx={{
          backgroundColor: CONFIG.colors.tooltipBg,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          p: 2,
          minWidth: 180,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.name}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={0.5}>
                <AttachMoney fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Amount(Rs.):
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                Rs.{payload[0].value.toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Percentage:
              </Typography>
              <Typography variant="body2">
                {percentage}%
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Days Overdue:
              </Typography>
              <Typography variant="body2">
                {dataItem.name === "Current" ? "0" : 
                 dataItem.name === "1-30 Days" ? "1-30" :
                 dataItem.name === "31-60 Days" ? "31-60" :
                 dataItem.name === "61-90 Days" ? "61-90" : "90+"}
              </Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const chartData = [
    {
      name: "Current",
      value: data.current,
      color: CONFIG.colors.primary[0]
    },
    {
      name: "1-30 Days",
      value: data.overdue30,
      color: CONFIG.colors.primary[1]
    },
    {
      name: "31-60 Days",
      value: data.overdue60,
      color: CONFIG.colors.primary[2]
    },
    {
      name: "61-90 Days",
      value: data.overdue90,
      color: CONFIG.colors.primary[3]
    },
    {
      name: "90+ Days",
      value: data.overdue120,
      color: CONFIG.colors.primary[4]
    }
  ];

  const barChartData = [
    {
      name: "Current",
      amount: data.current,
      color: CONFIG.colors.primary[0]
    },
    {
      name: "1-30",
      amount: data.overdue30,
      color: CONFIG.colors.primary[1]
    },
    {
      name: "31-60",
      amount: data.overdue60,
      color: CONFIG.colors.primary[2]
    },
    {
      name: "61-90",
      amount: data.overdue90,
      color: CONFIG.colors.primary[3]
    },
    {
      name: "90+",
      amount: data.overdue120,
      color: CONFIG.colors.primary[4]
    }
  ];

  const exportToCSV = () => {
    const headers = ["Ageing Bucket", "Amount", "Percentage"];
    const rows = chartData.map(item => [
      item.name,
      `Rs.${item.value.toLocaleString()}`,
      `${Math.round((item.value / stats.totalPayables) * 100)}%`
    ]);
    
    rows.push(["Total", `Rs.${stats.totalPayables.toLocaleString()}`, "100%"]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n" 
      + rows.map(row => row.join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "payable_ageing_analysis.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card sx={{ 
      height: "100%",
      minHeight: 450,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      transition: "all 0.3s ease",
      '&:hover': {
        boxShadow: theme.shadows[6]
      }
    }}>
      <CardContent sx={{ 
        p: isMobile ? 2 : 3,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              color: CONFIG.colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
              <Avatar sx={{ 
                bgcolor: colors.redAccent[500],
                width: 32,
                height: 32,
                boxShadow: theme.shadows[2],
              }}>
                <Paid fontSize="small" />
              </Avatar>
              Payable Ageing Analysis
              <MuiTooltip title="Breakdown of accounts payable by ageing buckets">
                <InfoOutlined sx={{ 
                  fontSize: 18,
                  color: CONFIG.colors.textSecondary,
                  cursor: "pointer"
                }}/>
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Breakdown of accounts payable by ageing buckets
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={viewMode === "bar" ? <TableChart /> : <BarChartIcon />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {viewMode === "bar" ? "Table" : "Bar"}
            </Button>
            
            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {error && (
          <Box>
            <Chip
              label={error}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{ 
                mb: 0.5,
                borderColor: colors.warning,
                color: colors.warning
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Payables
                  </Typography>
                  <Typography variant="h5" fontWeight={700} mt={0.5}>
                    Rs.{stats.totalPayables.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    Across all ageing buckets
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Current
                  </Typography>
                  <Typography variant="h5" fontWeight={700} mt={0.5}>
                    {stats.currentPercentage}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    Rs.{data.current.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" display="block" mt={0.5}>
                    {Math.round(data.current / stats.totalPayables * 100)}% of total
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: CONFIG.colors.cardBg,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                 
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    // backgroundColor: colors.blueAccent[500]
                  }} />
                  <Typography variant="body2" color="text.secondary">
                    Overdue
                  </Typography>
                  <Typography variant="h5" fontWeight={700} mt={0.5} sx={{
                    color: stats.overduePercentage > 30 ? colors.redAccent[500] : 
                           stats.overduePercentage > 15 ? colors.blueAccent[500] : colors.greenAccent[500]
                  }}>
                    {stats.overduePercentage}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    Rs.{(data.overdue30 + data.overdue60 + data.overdue90 + data.overdue120).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" display="block" mt={0.5} sx={{
                    color: colors.blueAccent[500]
                  }}>
                    Avg. {stats.avgOverdueDays} days overdue
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>

        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "bar" ? (
            <Box 
              height="100%" 
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={barChartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis type="number" tick={{ fill: theme.palette.text.primary }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={80} 
                    tick={{ fill: theme.palette.text.primary }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="amount" name="Amount (Rs.)">
                    {barChartData.map((entry, index) => (
                      <Cell key={`bar-cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ageing Bucket</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                    <TableCell align="right">Days Overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((bucket) => (
                    <TableRow key={bucket.name}>
                      <TableCell component="th" scope="row">{bucket.name}</TableCell>
                      <TableCell align="right">Rs.{bucket.value.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.round((bucket.value / stats.totalPayables) * 100)}%
                      </TableCell>
                      <TableCell align="right">
                        {bucket.name === "Current" ? "0" : 
                         bucket.name === "1-30 Days" ? "1-30" :
                         bucket.name === "31-60 Days" ? "31-60" :
                         bucket.name === "61-90 Days" ? "61-90" : "90+"}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Rs.{stats.totalPayables.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PayableAgeingAnalysis;